/* Cassaforte · generazione di un wallet Ethereum nel browser.
 *
 * `ethers` arriva con un `import()` dinamico, non dall'alto del file. Pesa
 * qualche centinaio di KB e serve a una cosa sola, premere «Genera»: caricarla
 * insieme all'applicazione farebbe pagare quel peso a ogni apertura della
 * dashboard, a chi non aprira' mai questa schermata. Cosi' invece Vite ne fa
 * un pezzo separato, scaricato al primo clic.
 *
 * La generazione sta nel browser e non sul server per la stessa ragione per
 * cui ci sta la cifratura: la chiave privata non deve esistere da nessuna
 * altra parte. Nasce qui, viene cifrata qui, e quello che parte nel POST e'
 * gia' testo cifrato.
 */

export interface WalletGenerato {
  address: string;
  privateKey: string;
  mnemonic: string;
}

export async function generaWallet(): Promise<WalletGenerato> {
  const { Wallet } = await import('ethers');
  const wallet = Wallet.createRandom();

  /* `mnemonic` e' opzionale nel tipo di ethers: un wallet costruito da una
     chiave privata non ne ha una. Quello creato da `createRandom()` ce l'ha
     sempre, ma il controllo resta perche' e' il tipo a dirlo, e una frase
     vuota salvata come se fosse buona e' un recupero che non funzionera'. */
  const frase = wallet.mnemonic?.phrase;
  if (!frase) {
    throw new Error('Il wallet generato non ha una frase mnemonica: non è utilizzabile per il recupero.');
  }

  return { address: wallet.address, privateKey: wallet.privateKey, mnemonic: frase };
}

/* ==========================================================================
 * Controlli di forma su quello che l'utente incolla
 * ======================================================================== */

/**
 * Se l'utente incolla invece di generare, questi controlli intercettano il
 * refuso prima che diventi un wallet cifrato e illeggibile. Non validano la
 * chiave (curva, checksum BIP-39): quella verifica costerebbe il caricamento
 * di `ethers` a ogni battuta, e qui serve solo a non salvare una riga storta.
 */
const PAROLE_MNEMONICA = [12, 15, 18, 21, 24];

export function indirizzoValido(valore: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(valore.trim());
}

export function chiaveValida(valore: string): boolean {
  return /^(0x)?[0-9a-fA-F]{64}$/.test(valore.trim());
}

export function mnemonicaValida(valore: string): boolean {
  const parole = valore.trim().split(/\s+/).filter(Boolean);
  return PAROLE_MNEMONICA.includes(parole.length) && parole.every((p) => /^[a-z]+$/.test(p));
}
